import { create } from 'zustand'
import { ExternalAccount } from 'types/api/me'
import { persist, createJSONStorage } from 'zustand/middleware'
import { TransformedConceptItem } from 'pages/cashout/NewTransfer'

/* --------------------------- NEW TRANSFER STORE --------------------------- */
/* 
store all the options selected by the user to show
on the transference confirmation page
*/
interface Account extends ExternalAccount {
  key: string
  name: string
}

type NewTransferState = {
  concept: TransformedConceptItem
  detail: string
  amount: string
  account: Account
}

type NewTransferActions = {
  updateConcept: (c: TransformedConceptItem) => void
  updateDetail: (d: string) => void
  updateAmount: (a: string) => void
  updateToDefault: () => void
}

export const useNewTransferStore = create<
  NewTransferState & NewTransferActions
>()(
  persist(
    (set) => ({
      account: null,
      amount: '0',
      concept: null,
      detail: null,
      updateConcept: (c: TransformedConceptItem) => set(() => ({ concept: c })),
      updateDetail: (d: string) => set(() => ({ detail: d })),
      updateAmount: (a: string) => set(() => ({ amount: a })),
      updateToDefault: () => {
        set(() => ({ account: null }))
        set(() => ({ amount: '0' }))
        set(() => ({ concept: null }))
        set(() => ({ detail: null }))
      }
    }),
    {
      name: 'new-transfer-storage',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- ACTIVITY STORE ----------------------------- */
/* 
use for storing the selected activity ID 
by the user and get all the details to show
*/

type ActivityIDState = {
  activityID: string
}

type ActivityIDAction = {
  updateActivityID: (id: string) => void
}

export const useActivityIDStore = create<ActivityIDState & ActivityIDAction>()(
  persist(
    (set) => ({
      activityID: null,
      updateActivityID: (id: string) => set(() => ({ activityID: id }))
    }),
    {
      name: 'act-id',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- SELECTED ACCOUNT ----------------------------- */
/* 

*/

type SelectedAccount = {
  CBU?: string
  CVU?: string
  CUIL: string | number
  accountHolder: string
  comesFromNewAccount?: boolean
}

type EntityData = {
  bankCode?: string | number
  bankName: string
  iconURL: string | null
}

type SelectedAccountState = {
  selectedAccount: SelectedAccount | null
  entityData: EntityData | null
}

type SelectedAccountAction = {
  updateEntityData: (entityData: EntityData) => void
  updateSelectedAccount: (selectedAccount: SelectedAccount) => void
}

export const useSelectedAccountStore = create<
  SelectedAccountState & SelectedAccountAction
>()(
  persist(
    (set) => ({
      entityData: null,
      selectedAccount: null,
      updateEntityData: (entityData: EntityData) => set(() => ({ entityData })),
      updateSelectedAccount: (selectedAccount: SelectedAccount) =>
        set(() => ({ selectedAccount }))
    }),
    {
      name: 'selected-account',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- SELECTED ACCOUNT ----------------------------- */
/* 
chosen contact from list. this is used for getting this person's accounts in
recipient page.
*/

type SelectedContact = {
  selectedContact: string
}

type SelectedContactAction = {
  updateSelectedContact: (id: string) => void
}

export const useSelectedContactStore = create<
  SelectedContact & SelectedContactAction
>()(
  persist(
    (set) => ({
      selectedContact: null,
      updateSelectedContact: (selectedContact: string) =>
        set(() => ({ selectedContact }))
    }),
    {
      name: 'selected-contact',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/* ----------------------------- CASHOUT SUCCESS RESPONSE ----------------------------- */
/* 
save the response of a succesful cashout to show it in success page.
*/
type APIRes = {
  status: string
  message: string
  uuid: string
  tax_type: string
  tax_value: string
  tax_identification_value: string
  amount: string
  currency: string
  bank_name: string
  account_holder: string
  bank_image?: string
}

type CashoutResponse = {
  response: APIRes
}

type CashoutResponseAction = {
  updateResponse: (res: Object) => void
}

export const useCashoutResponse = create<
  CashoutResponse & CashoutResponseAction
>()(
  persist(
    (set) => ({
      response: null,
      updateResponse: (response: APIRes) => set(() => ({ response }))
    }),
    {
      name: 'selected-contact',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)
